import { Meteor } from 'meteor/meteor';

/**
 * Connecting restaurants from your Google accounts (docs/19 section 5): the
 * shapes the backend returns and the calls the page, the wizard and the
 * confirmation sheet make. The server checks every rule again; nothing here
 * decides who may connect what.
 */

export type GoogleProductKind = 'ga4' | 'ads' | 'gsc' | 'gbp';

export type RestaurantOption = { id: string; name: string; city?: string };

/** What a property feeds now (venues named where the person can open them). */
export type PropertyFeeds = {
  venues: Array<{ id: string; name: string }>;
  othersCount: number;
  since?: number | null;
  by?: { name: string | null; isStaff: boolean | null; isYou: boolean; departed: boolean } | null;
  /** Staff only. */
  orgId?: string;
  customerName?: string | null;
  connectionName?: string | null;
};

export type AccountProperty = {
  kind: GoogleProductKind;
  externalId: string;
  displayName: string;
  accountLabel?: string;
  state: 'feeds' | 'suggested' | 'none';
  feeds: PropertyFeeds | null;
  suggestions: RestaurantOption[];
};

export type GoogleAccount = {
  credentialId: string;
  /** Its owner's name for it; never an email. */
  name: string | null;
  products: GoogleProductKind[];
  status: string;
  refreshable?: boolean;
  createdAt?: number | null;
  discoveredAt?: number | null;
  problem?: 'reconnect' | null;
  properties: AccountProperty[];
};

export type GoogleAccountsPayload = { accounts: GoogleAccount[]; viewerIsStaff: boolean };

export type ConnectTarget = {
  property: { kind: GoogleProductKind; externalId: string; displayName: string };
  productGranted: boolean;
  accountName: string | null;
  connectedByName: string | null;
  target: null | {
    orgId: string | null;
    customerName: string | null;
    /** The first venue's customer's venues the person can open (rule 3). */
    venues: RestaurantOption[];
    alreadyRestaurantIds: string[];
    alreadyVia: { connectionName: string | null; yours: boolean } | null;
    feedsOtherCustomer: boolean;
    grantExists: boolean;
    connectionName: string | null;
  };
};

export type ConnectUndo = { grantId: string; resourceId: string; restaurantIds: string[] };

export type ConnectResult = {
  ok: boolean;
  status: 'connected' | 'already';
  orgId: string;
  grantId?: string;
  grantCreated?: boolean;
  connectionName?: string | null;
  resourceId?: string | null;
  restaurantIds: string[];
  undo?: ConnectUndo;
};

/** The property a sheet connects and where it may start. */
export type ConnectRequest = {
  credentialId: string;
  property: { kind: GoogleProductKind; externalId: string; displayName: string };
  /** A hint (a suggestion or the context's venue); never applied without a click. */
  initialRestaurantId?: string | null;
  /** Restaurants that look like the property, listed first in the picker. */
  suggestions?: RestaurantOption[];
};

export function fetchGoogleAccounts(restaurantId: string): Promise<GoogleAccountsPayload> {
  return Meteor.callPromise<GoogleAccountsPayload>('opsCommand/getGoogleAccounts', restaurantId);
}

export function fetchConnectTarget(
  restaurantId: string,
  request: ConnectRequest,
  targetRestaurantId: string | null,
): Promise<ConnectTarget> {
  return Meteor.callPromise<ConnectTarget>(
    'opsCommand/getGoogleConnectTarget',
    restaurantId,
    request.credentialId,
    request.property.kind,
    request.property.externalId,
    targetRestaurantId,
  );
}

export function connectProperty(
  restaurantId: string,
  request: ConnectRequest,
  restaurantIds: string[],
  connectionName: string | null,
): Promise<ConnectResult> {
  return Meteor.callPromise<ConnectResult>(
    'opsCommand/connectGoogleProperty',
    restaurantId,
    request.credentialId,
    request.property.kind,
    request.property.externalId,
    restaurantIds,
    connectionName,
  );
}

export function undoConnect(restaurantId: string, undo: ConnectUndo): Promise<{ grantRemoved: boolean }> {
  return Meteor.callPromise<{ grantRemoved: boolean }>(
    'opsCommand/undoGooglePropertyConnect',
    restaurantId,
    undo.grantId,
    undo.resourceId,
    undo.restaurantIds,
  );
}

export function renameGoogleAccount(
  restaurantId: string,
  credentialId: string,
  name: string,
): Promise<{ name: string | null }> {
  return Meteor.callPromise<{ name: string | null }>(
    'opsCommand/renameGoogleAccount',
    restaurantId,
    credentialId,
    name,
  );
}

export function fetchRestaurantOptions(): Promise<RestaurantOption[]> {
  return Meteor.callPromise<{ restaurants?: RestaurantOption[] }>(
    'opsCommand/listAccessibleRestaurants',
  ).then(res => res.restaurants || []);
}

/** "Kronenhalle", "Kronenhalle · Zürich". */
export function restaurantLabel(option: RestaurantOption | null | undefined): string {
  if (!option) return '';
  return option.city ? `${option.name} · ${option.city}` : option.name;
}

/** "A", "A and B", "A, B and C", in the person's language. */
export function joinNames(names: string[], and: string): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;
}

/** A customer's name to show, or null (lib/customerName). */
export { customerLabel } from '../lib/customerName';
